import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRegister } from '@/api/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

export function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [consents, setConsents] = useState({
    analysis: true,
    deidentified_research: true,
    email_reminders: false,
    marketing: false,
  });
  const [validationError, setValidationError] = useState('');
  const register = useRegister();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (password !== confirmPassword) {
      setValidationError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters.');
      return;
    }

    try {
      await register.mutateAsync({ email, password, consents });
      navigate('/', { replace: true });
    } catch {
      // error is available via register.error
    }
  };

  const displayError = validationError || register.error?.message;

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy font-sans px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="w-full max-w-md"
      >
        <div className="rounded-2xl bg-white p-8 shadow-xl">
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-copper font-bold text-white text-xl mb-3">
              M
            </div>
            <h1 className="text-2xl font-bold text-navy">Create your account</h1>
            <p className="mt-1 text-sm text-gray-500">
              Upload your labs and see what the research says
            </p>
          </div>

          {/* Error */}
          {displayError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
            >
              {displayError}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Repeat your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>

            {/* Consent checkboxes */}
            <div className="space-y-3 rounded-lg bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-3">
                Data Consent
              </p>

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={consents.analysis}
                  onCheckedChange={(v) =>
                    setConsents((c) => ({ ...c, analysis: v }))
                  }
                  disabled
                />
                <span className="text-sm text-gray-700 leading-snug">
                  I consent to automated analysis of my bloodwork data{' '}
                  <span className="text-gray-400">(required)</span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={consents.deidentified_research}
                  onCheckedChange={(v) =>
                    setConsents((c) => ({ ...c, deidentified_research: v }))
                  }
                  disabled
                />
                <span className="text-sm text-gray-700 leading-snug">
                  I consent to de-identified data use for research{' '}
                  <span className="text-gray-400">(required)</span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={consents.email_reminders}
                  onCheckedChange={(v) =>
                    setConsents((c) => ({ ...c, email_reminders: v }))
                  }
                />
                <span className="text-sm text-gray-700 leading-snug">
                  Send me re-test reminders via email
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={consents.marketing}
                  onCheckedChange={(v) =>
                    setConsents((c) => ({ ...c, marketing: v }))
                  }
                />
                <span className="text-sm text-gray-700 leading-snug">
                  Send me supplement and product recommendations
                </span>
              </label>
            </div>

            <Button
              type="submit"
              className="w-full h-11"
              disabled={register.isPending || !email || !password || !confirmPassword}
            >
              {register.isPending ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Creating account...
                </span>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-copper hover:text-copper-dark transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
